import type { NextApiRequest, NextApiResponse } from 'next'
import {
  type Currency,
  type Language,
  type NshiftDeliveryOptionsRequest,
  type NshiftDeliveryOptionsRequestFE,
  type NshiftDeliveryOptionsResponse,
  type OldProductDTO,
  type ShippingProviderConfig
} from 'ecosystem'
import { createError, nshiftApi, useLocaleCredentials as setLocaleCredentials } from 'api'
import storefrontApi from 'api/storefront-api'
import * as productsApi from 'api/storefront-api/productsEndpoint'
import { calculateOrderTotalWeight, getFullLocale } from 'shared-utils'
import { handlerWithLogging } from '../../../logging'

interface NshiftGetDeliveryOptionsHandlerOpts {
  shippingProviderConfig?: ShippingProviderConfig
  locale?: Language
  currencyCode: Currency
  customerId?: string | null
}

export const nshiftGetDeliveryOptionsHandler = handlerWithLogging(
  async (
    req: NextApiRequest,
    res: NextApiResponse<NshiftDeliveryOptionsResponse | { error: string }>,
    opts: NshiftGetDeliveryOptionsHandlerOpts
  ) => {
    const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    const locale = opts.locale || localeForCredentials

    setLocaleCredentials(localeForCredentials)

    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }

    const {
      cartTotalAmount = 0,
      storefrontOrderNumber,
      receiver
    } = req.body as NshiftDeliveryOptionsRequestFE

    let responseError: string | null

    try {
      const { data: order, error: orderError } = await storefrontApi.orders.getStorefrontOrder({
        orderNumber: storefrontOrderNumber
      })

      if (!order || orderError) {
        throw createError(orderError || 'Get storefront order unknown error')
      }

      if (!order.shippingId) {
        throw createError('No session id found')
      }

      const fullProducts: OldProductDTO[] = await (async () => {
        const productIds = order.items?.map((item) => item.product.id) ?? []

        if (!productIds?.length) {
          return []
        }

        const productsResponse = await productsApi.listProductsFull(
          {
            productIds,
            ...(opts.customerId ? { customerId: opts.customerId } : {})
          },
          {
            size: productIds.length
          }
        )

        return productsResponse.content
      })()

      if (fullProducts.length !== order.items?.length) {
        throw new Error('Products are not found')
      }

      const { totalWeight, extendedItems } = calculateOrderTotalWeight(order.items, fullProducts)

      const payload: NshiftDeliveryOptionsRequest = {
        sessionId: order.shippingId,
        currencyCode: opts.currencyCode,
        languageCode: locale,
        localeId: getFullLocale(locale),
        totalPrice: cartTotalAmount,
        receiver,
        packages: [
          {
            weightKg: totalWeight,
            articles: extendedItems.map((item) => ({
              articleNo: item.product.articleNumber,
              quantity: item.quantity,
              weightKg: item.weight
            }))
          }
        ]
      }

      const { data, error } = await nshiftApi.checkout.getDeliveryOptions(payload)

      if (data) {
        res.status(200).json(data)
        return
      }

      responseError = error ?? 'Data is not found'
    } catch (e) {
      responseError = e instanceof Error ? e.message : 'unknown'
    }

    res.status(400).json({ error: responseError })
  }
)
